import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SingleCard from "./SingleCard";
import Pagination from "../Pagination";
import Breadcrumb from "../Breadcrumbs";
import {
  Alert,
  Box,
  Grid,
  Typography,
  Button,
  Chip,
  Skeleton,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import AnalyticsTracker from "../GASetup/AnalyticsTracker";
import Filters from "./AdsFilter/Index";
import LoadingAnimation from "../Animations/LoadingAnimation";
import {
  applyFilterButtons,
  cardHover,
  cardOutline,
  clearFilterButtons,
  fonts,
  headingFonts,
  notFoundBox,
  semiBoldFonts,
} from "../../Styles/Styles";

export default function AdsPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [totalAds, setTotalAds] = useState(0);
  const [adsData, setAdsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [resetKey, setResetKey] = useState(0);

  const adsPerPage = 12;

  // Extract filters from URL
  const pageFromURL = Number(searchParams.get("p")) || 1;
  let locationFromURL = searchParams.get("location") || "";
  let queryFromURL = searchParams.get("query") || "";
  let priceFromURL = searchParams.get("priceRange")
    ? searchParams.get("priceRange").split(",").map(Number)
    : [];

  // States for applied filters
  const [currentPage, setCurrentPage] = useState(pageFromURL);
  const [location, setLocation] = useState(locationFromURL);
  const [priceFilter, setPriceFilter] = useState(priceFromURL);
  const [queryFilter, setQueryFilter] = useState(queryFromURL);

  // State for pending filters before applying
  const [pendingFilters, setPendingFilters] = useState({
    location: locationFromURL,
    priceRange: priceFromURL,
    query: queryFromURL,
  });

  const fetchAds = async () => {
    setLoading(true);
    setError(null);

    let apiUrl;
    let requestOptions = {};

    const priceQuery =
      priceFilter.length > 0 && (priceFilter[0] || priceFilter[1])
        ? priceFilter.join(",")
        : null;

    if (id) {
      apiUrl = `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/category`;
      requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: id,
          page: pageFromURL,
          limit: adsPerPage,
          priceRange: priceQuery,
          location: location,
          query: queryFilter,
        }),
      };
    } else {
      apiUrl = `${process.env.REACT_APP_API_SERVER_BASE_URL}/ads/allads`;
      const queryParams = new URLSearchParams();
      queryParams.set("page", pageFromURL);
      queryParams.set("limit", adsPerPage);
      if (priceQuery) queryParams.set("priceRange", priceQuery);
      if (location) queryParams.set("location", location);
      if (queryFilter) queryParams.set("query", queryFilter);

      apiUrl += `?${queryParams.toString()}`;
    }

    try {
      const response = await fetch(apiUrl, id ? requestOptions : {});
      if (!response.ok) throw new Error("Failed to fetch ads");

      const data = await response.json();
      setAdsData(data.ads);
      setTotalAds(data.totalAds);
      setTotalPages(data.totalPages);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch ads when URL params change
  useEffect(() => {
    fetchAds();
  }, [id, searchParams, resetKey]);

  // Handle filter selection before applying
  const handleFilterChange = (filters) => {
    setPendingFilters((prev) => ({
      ...prev,
      ...filters,
    }));
  };

  // Apply filters and update URL params
  const applyFilters = () => {
    const newSearchParams = new URLSearchParams();

    if (
      pendingFilters.priceRange.length > 0 &&
      (pendingFilters.priceRange[0] || pendingFilters.priceRange[1])
    ) {
      newSearchParams.set("priceRange", pendingFilters.priceRange.join(","));
      setPriceFilter(pendingFilters.priceRange);
    } else {
      setPriceFilter([]); // Reset price filter state
    }

    if (pendingFilters.location) {
      newSearchParams.set("location", pendingFilters.location);
      setLocation(pendingFilters.location);
    } else {
      setLocation(""); // Reset location filter state
    }

    if (pendingFilters.query) {
      newSearchParams.set("query", pendingFilters.query);
      setQueryFilter(pendingFilters.query);
    } else {
      setQueryFilter("");
    }

    newSearchParams.set("p", 1); // Reset pagination on filter change
    setSearchParams(newSearchParams);
  };

  const removeFilter = (key, index = null) => {
    setPendingFilters((prev) => {
      const updatedFilters = { ...prev };

      if (key === "priceRange") {
        if (index === 0) {
          // Remove only min price (keep max price)
          updatedFilters.priceRange = ["", updatedFilters.priceRange[1]];
        } else if (index === 1) {
          // Remove only max price (keep min price)
          updatedFilters.priceRange = [updatedFilters.priceRange[0], ""];
        }
      } else {
        updatedFilters[key] = ""; // Clear location
      }

      // If all filters are cleared, reset the state
      if (
        !updatedFilters.location &&
        !updatedFilters.query &&
        !updatedFilters.priceRange[0] &&
        !updatedFilters.priceRange[1]
      ) {
        setPendingFilters({
          location: "",
          priceRange: [],
          query: "",
        });
      }

      return updatedFilters;
    });
  };

  const clearFilters = () => {
    setPendingFilters({
      location: "",
      priceRange: [],
      query: "",
    });

    setLocation("");
    setQueryFilter("");
    setPriceFilter([]);

    setSearchParams({ p: 1 }); // Reset pagination
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSearchParams({ p: newPage });
  };

  return (
    <>
      <Helmet>
        <title>
          Listed.ky -{" "}
          {id ? `${id} in Cayman Islands` : "All Ads in Cayman Islands"}
        </title>
        <meta
          name="description"
          content={id ? `${id} in Cayman Islands` : "All Ads in Cayman Islands"}
        />
      </Helmet>

      <AnalyticsTracker />
      <Breadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: id ? `${id}` : "Ads", path: "#" },
        ]}
      />

      <Box sx={{ maxWidth: "95%", margin: "auto", mt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" component="h4" sx={{ ...semiBoldFonts }}>
            {id
              ? `${id.replace(/-/g, " ")} in Cayman Islands`
              : `All Ads in Cayman Islands `}
            {loading ? (
              <Skeleton
                variant="text"
                width={60}
                height={40}
                sx={{ display: "inline-block", verticalAlign: "middle" }}
              />
            ) : (
              `(${totalAds})`
            )}
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Filters - Left Side */}
          <Grid item xs={12} md={3}>
            <Filters
              key={resetKey}
              onFilterChange={handleFilterChange}
              defaultFilters={pendingFilters}
            />
          </Grid>

          {/* Ads - Right Side */}
          <Grid item xs={12} md={9} mb={6}>
            {/* Applied Filters - Chips */}
            {(pendingFilters.location ||
              pendingFilters.priceRange.some((val) => val) ||
              pendingFilters.query) && (
              <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {pendingFilters.query && (
                  <Chip
                    label={`Search: ${pendingFilters.query}`}
                    onDelete={() => removeFilter("query")}
                    sx={{
                      ...fonts,
                      ...cardOutline,
                      ...cardHover,
                    }}
                  />
                )}
                {pendingFilters.location && (
                  <Chip
                    label={`Location: ${pendingFilters.location}`}
                    onDelete={() => removeFilter("location")}
                    sx={{
                      ...fonts,
                      ...cardOutline,
                      ...cardHover,
                    }}
                  />
                )}
                {pendingFilters.priceRange[0] && (
                  <Chip
                    label={`Min Price: ${pendingFilters.priceRange[0]}`}
                    onDelete={() => removeFilter("priceRange", 0)}
                    sx={{
                      ...fonts,
                      ...cardOutline,
                      ...cardHover,
                    }}
                  />
                )}
                {pendingFilters.priceRange[1] && (
                  <Chip
                    label={`Max Price: ${pendingFilters.priceRange[1]}`}
                    onDelete={() => removeFilter("priceRange", 1)}
                    sx={{
                      ...fonts,
                      ...cardOutline,
                      ...cardHover,
                    }}
                  />
                )}

                <Button
                  onClick={applyFilters}
                  className="main-button-style"
                  sx={{ ...applyFilterButtons }}
                >
                  <>
                    <span>Apply Filters</span>
                    <FilterAltOutlinedIcon />
                  </>
                </Button>
                {/* Clear All Filters Button */}
                <Button
                  onClick={clearFilters}
                  className="main-button-style"
                  sx={{ ...clearFilterButtons }}
                >
                  <span>Clear All</span>
                  <FilterAltOffOutlinedIcon />
                </Button>
              </Box>
            )}

            {/* Ads Content */}
            {error && <Alert severity="error">{error}</Alert>}
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <LoadingAnimation />
              </Box>
            ) : adsData.length > 0 ? (
              <>
                <Grid container spacing={2}>
                  {adsData.map((AdInfo, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <SingleCard AdInfo={AdInfo} />
                    </Grid>
                  ))}
                </Grid>
                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            ) : (
              <Box sx={{ ...notFoundBox }}>
                <SearchOffOutlinedIcon
                  sx={{
                    color: "var(--primary-color)",
                    fontSize: 50,
                    opacity: 0.5,
                    marginBottom: 1,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: "var(--primary-color)",
                    ...headingFonts,
                  }}
                >
                  No ads found.
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Try adjusting your filters or check back later.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
